import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Plus,
  Share2,
  Users,
  MapPin,
  MapPinOff,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from 'lucide-react'

interface HomeBase {
  name: string
  displayName: string
}

interface CurrentUser {
  displayName: string
  avatarUrl: string | null
}

export type Scope = 'me' | 'friends' | 'all'
type CycleDirection = 'back' | 'forward'

interface StashBoardHeaderProps {
  scope?: Scope
  homeBase?: HomeBase | null
  currentUser?: CurrentUser | null
  nearMeEnabled?: boolean
  onToggleNearMe?: () => void
  onAddCard?: () => void
  onShareApp?: () => void
  onCreateGroup?: () => void
  onCycleHomeBase?: (direction: CycleDirection) => void
  onProfileClick?: () => void
}

export function StashBoardHeader({
  scope = 'all',
  homeBase = null,
  currentUser = null,
  nearMeEnabled = false,
  onToggleNearMe,
  onAddCard,
  onShareApp,
  onCreateGroup,
  onCycleHomeBase,
  onProfileClick,
}: StashBoardHeaderProps) {
  return (
    <header className="px-4 pt-4 pb-2 md:px-6 lg:px-8">
      {/* HomeBase swipe indicator (desktop: arrows) */}
      {homeBase && (
        <div className="flex items-center justify-center gap-2 mb-1">
          <button
            onClick={() => onCycleHomeBase?.('back')}
            className="p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Previous home base"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
            {homeBase.name}
          </span>
          <button
            onClick={() => onCycleHomeBase?.('forward')}
            className="p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Next home base"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Scope-specific header */}
      {scope === 'me' && currentUser ? (
        <MyStashHeader user={currentUser} onProfileClick={onProfileClick} homeBase={homeBase} />
      ) : scope === 'friends' ? (
        <FriendsStashHeader homeBase={homeBase} />
      ) : (
        <AllStashHeader homeBase={homeBase} nearMeEnabled={nearMeEnabled} onToggleNearMe={onToggleNearMe} />
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-center gap-2 mt-3">
        <ActionButton icon={Plus} label="Add Card" onClick={onAddCard} />
        {scope !== 'me' && (
          <>
            <ActionButton icon={Share2} label="Share STASH" onClick={onShareApp} />
            <ActionButton icon={Users} label="Create Group" onClick={onCreateGroup} />
          </>
        )}
      </div>
    </header>
  )
}

interface MyStashHeaderProps {
  user: CurrentUser
  onProfileClick?: (() => void) | undefined
  homeBase: HomeBase | null
}

function MyStashHeader({ user, onProfileClick, homeBase }: MyStashHeaderProps) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onProfileClick} className="shrink-0 group">
        <Avatar className="h-11 w-11 ring-2 ring-[var(--stash-blue)]/30 group-hover:ring-[var(--stash-blue)]/60 transition-all">
          <AvatarImage src={user.avatarUrl ?? undefined} alt={user.displayName} className="" />
          <AvatarFallback className="bg-[var(--stash-blue)] text-white font-semibold text-sm">
            {user.displayName?.charAt(0)}
          </AvatarFallback>
        </Avatar>
      </button>
      <div className="min-w-0">
        <p className="text-xs font-medium text-muted-foreground">
          My {homeBase ? <span className="font-bold">{homeBase.name}</span> : ''} STASH Board
        </p>
        <h1 className="text-xl font-bold tracking-tight truncate">{user.displayName}</h1>
      </div>
    </div>
  )
}

interface FriendsStashHeaderProps {
  homeBase: HomeBase | null
}

function FriendsStashHeader({ homeBase }: FriendsStashHeaderProps) {
  return (
    <div className="text-center">
      <p className="text-xs font-medium text-muted-foreground mb-0.5">Your Friends'</p>
      <h1 className="text-2xl font-bold tracking-tight">
        {homeBase ? `${homeBase.name} ` : ''}STASH Board
      </h1>
    </div>
  )
}

interface AllStashHeaderProps {
  homeBase: HomeBase | null
  nearMeEnabled: boolean
  onToggleNearMe?: (() => void) | undefined
}

function AllStashHeader({ homeBase, nearMeEnabled, onToggleNearMe }: AllStashHeaderProps) {
  return (
    <div className="text-center">
      <p className="text-xs font-medium text-muted-foreground mb-0.5">All</p>
      <h1 className="text-2xl font-bold tracking-tight">
        {homeBase ? `${homeBase.name} ` : ''}STASH Board
      </h1>

      {/* Near Me toggle */}
      <button
        onClick={onToggleNearMe}
        className={`
          inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-full text-xs font-medium
          transition-all duration-200
          ${nearMeEnabled
            ? 'bg-[var(--stash-blue)]/10 text-[var(--stash-blue)] hover:bg-[var(--stash-blue)]/20'
            : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }
        `}
      >
        {nearMeEnabled ? (
          <MapPin className="w-3.5 h-3.5" />
        ) : (
          <MapPinOff className="w-3.5 h-3.5" />
        )}
        {nearMeEnabled ? 'Near Me' : 'Everywhere'}
      </button>
    </div>
  )
}

interface ActionButtonProps {
  icon: LucideIcon
  label: string
  onClick?: (() => void) | undefined
}

function ActionButton({ icon: Icon, label, onClick }: ActionButtonProps) {
  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={onClick}
      className="gap-1.5 rounded-full px-3 sm:px-4 h-8 sm:h-9 font-medium text-xs shadow-sm hover:shadow-md transition-shadow"
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </Button>
  )
}
